import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import axios from "axios";
import { useDebounce } from "use-debounce";
import { Tooltip } from "react-tooltip";
import PulseLoader from "react-spinners/PulseLoader";
import { DropdownWithSearch, TextInput } from "hesus-ui-kit";
import { getDateFormat } from "@utils/datePickerUtils";
import { documentBasedEntityTypeOption } from "@utils/constants";
import DocumentType from "./components/DocumentType/DocumentType";
import DocumentAction from "./components/DocumentAction/DocumentAction";
import DocumentUpload from "./components/DocumentUpload/DocumentUpload";
import DocumentPreview from "../DocumentPreview/DocumentPreview";
import PaginationData from "@shared/baseUI/PaginationData/PaginationData";
import { DivToolTip, searchInputStyles } from "@utils/extra-style";

import {
  OuterWrapper,
  FlexRow,
  FilterFlexRow,
  SectionContainer,
  GridRow,
  ProductContainer,
  FilterOuter,
  GlobalSearch,
  AuthorContainer,
  NoDocument,
  GridLoaderItem,
  DocumentName,
} from "./DocumentList.styled";

import "./DocumentList.scss";

const ViewData = (props) => {
  const { t, entityId, entityType, entityData } =
    props;

  const [data, setData] = useState([]);
  const [isRefreshDataAgain, setRefreshState] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageStart, setPageStart] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState("");
  const [sort, setSortOption] = useState({ dir: "asc", column: "id" });

  const [documentFilterValue, setDocumentFilterValue] = useState([]);

  const [query, setQuery] = useState(search);
  const [debounceQuery] = useDebounce(query, 700);

  let documentTypeOptions = documentBasedEntityTypeOption(t, "");

  if (entityType === "siteId") {
    documentTypeOptions = documentBasedEntityTypeOption(t, "siteId");
  }
  if (entityType === "requestId") {
    documentTypeOptions = documentBasedEntityTypeOption(t, "requestId");
  }
  if (entityType === "companyId") {
    documentTypeOptions = documentBasedEntityTypeOption(t, "companyId");
  }
  /**
   * Method to fetch set of records based on the filters applied.
   * @param {pageSize} pageSize
   * @param {pageStart} pageStart
   * @param {globalFilter} globalFilter
   * @param {sort} sort
   */
  const fetchRecords = ({
    pageSize,
    pageStart,
    search,
    sort,
    documentFilterValue,
    currentPage,
  }) => {
    // save request data,

    // Set the loading state
    setLoading(true);
    let typeFilter = [];
    documentFilterValue.map((item) => {
      item.value !== "*" && typeFilter.push(item.value);
    });
    axios
      .get(
        `/documents?${entityType}=${entityId}&type=${typeFilter}&limit=${pageSize}&page=${currentPage}&orderBy=${
          sort ? sort.dir : "asc"
        }&orderColumn=${sort ? sort.column : "id"}&search=${search ?? ""}`,
        {
          headers: {
            Accept: "application/json",
          },
        }
      )
      .then((res) => {
        setPageCount(res.data.filteredRecords);
        setData(res.data.documents);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (isRefreshDataAgain) {
      setRefreshState(false);
    }
  }, [isRefreshDataAgain]);

  useEffect(() => {
    if (!loading) {
      fetchRecords({
        pageSize,
        pageStart,
        search,
        sort,
        documentFilterValue,
        currentPage,
      });
    }
  }, [
    currentPage,
    pageSize,
    pageStart,
    documentFilterValue,
    search,
    isRefreshDataAgain,
  ]);

  const handleChange = (e) => {
    setQuery(e.target.value || undefined);
  };

  useEffect(() => {
    setSearch(query);
    setCurrentPage(1);
  }, [debounceQuery]);

  // const globalSearchFilter = (e) => {
  //   setSearch(e.target.value);
  //   setCurrentPage(1);
  // };

  const onDocumentTypeFilterChange = (value) => {
    setDocumentFilterValue(value);
  };

  const openPreview = (document) => setSelectedDocument(document);
  const closePreview = () => setSelectedDocument(null);

  return (
    <>
      <OuterWrapper>
        <FlexRow>
          <SectionContainer>
            <div>
              <FilterFlexRow>
                <FilterOuter>
                  <DropdownWithSearch
                    label={t("DOCUMENTS_FILTER_TYPE")}
                    placeholder={t("DOCUMENTS_FILTER_TYPE")}
                    name="document-type-filter"
                    labelClass="document-list__quote-status-label"
                    containerClass="document-list__quote-status-dropdown-container"
                    containerStyles={{ minWidth: "12rem" }}
                    value={documentFilterValue || ""}
                    defaultOption="Nothing Selected"
                    options={documentTypeOptions}
                    onChange={onDocumentTypeFilterChange}
                  />
                </FilterOuter>
                <GlobalSearch>
                  <div className="search-box">
                    <TextInput
                      id="document-list-search"
                      type="search"
                      inputClass="search-box-default-input"
                      value={query || ""}
                      onChange={(e) => handleChange(e)}
                      placeholder=""
                      name="global-filter"
                      inputStyles={searchInputStyles}
                    />
                  </div>
                </GlobalSearch>
                <DocumentUpload
                  entityId={entityId}
                  entityType={entityType}
                  entityDataType={null}
                  setData={setData}
                  entityData={entityData}
                />
              </FilterFlexRow>
            </div>
            <div>
              {loading && (
                <GridRow column="1fr">
                  <GridLoaderItem>
                    <PulseLoader width="100%" color="#1bace4" />
                  </GridLoaderItem>
                </GridRow>
              )}
              <GridRow column="repeat(auto-fit, minmax(300px, 430px));">
                {data?.length > 0 &&
                  data?.map((data, index) => {
                    return (
                      <ProductContainer key={index}>
                        <GridRow column="80% auto" gap="0rem">
                          <GridRow gap="0rem">
                            <GridRow column="auto 1fr" gap="0rem">
                              <DocumentType type={data.type} />

                              <DocumentName
                                onClick={() => openPreview(data)}
                                id={`document-tooltip-id${data.id}`}
                                className="document-list__document-link"
                              >
                                {data?.fileName}
                              </DocumentName>

                              <Tooltip
                                anchorSelect={`#document-tooltip-id${data.id}`}
                                style={DivToolTip}
                                content={data.fileName}
                              />
                            </GridRow>
                            <AuthorContainer>
                              {"(" +
                                getDateFormat(data.createdAt,"YYYY-MM-DD") +
                                " " +
                                t("GENERAL_BY") +
                                " " +
                                (data.createdBy !== null
                                  ? data.createdBy?.firstName +
                                    " " +
                                    data.createdBy?.lastName
                                  : t("UNKNOWN")) +
                                ")"}
                            </AuthorContainer>
                          </GridRow>
                          <GridRow>
                            <DocumentAction
                              data={data}
                              onRefreshData={setRefreshState}
                            />
                          </GridRow>
                        </GridRow>
                      </ProductContainer>
                    );
                  })}
              </GridRow>
              {data?.length === 0 && (
                <GridRow column="1fr">
                  <NoDocument>{t("NO_DOCUMENT")}</NoDocument>
                </GridRow>
              )}
            </div>
          </SectionContainer>
        </FlexRow>

        {data?.length !== 0 && (
          <PaginationData
            pageCount={parseInt(pageCount, 10)}
            pageSize={pageSize}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            pageStart={pageStart}
            setPageStart={setPageStart}
            setPageSize={setPageSize}
          />
        )}
        {selectedDocument && (
          <DocumentPreview document={selectedDocument} onClose={closePreview} />
        )}
      </OuterWrapper>
    </>
  );
};

ViewData.propTypes = {
  t: PropTypes.func.isRequired,
  isRefreshList: PropTypes.bool.isRequired,
};

export default withTranslation()(ViewData);
